import React, { useMemo, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { RadioField, TextField } from 'src/jbc-front/components/Form';
import { useTranslation } from 'react-i18next';
import { FormSection, formValueSelector } from 'redux-form';
import selector from 'src/utils/Utils';
import { getItem } from 'src/reducers/common/itemReducer';
import { REDUCER_NAME } from 'src/reducers/employees/employees';
import { EMPLOYEE_FORM } from 'src/constants/FormNames';
import { getRadioOptions, getSelectOptions } from 'src/reducers/selectOptions';
import { isCorporateNumber, insuranceRate, isThaiMoney } from 'src/utils/CustomValidators';
import { SOCIAL_SECURITY_TARGETS } from 'src/constants/Options';
import { toDisplayFormat, toDbFormat } from 'src/utils/Completion';
import styles from './BasicInfoSection.scss';
import socialSecurityStyles from './SocialSecurityContributionSection.scss';

const SocialSecurityContributionSection = props => {
  const DEFAULT_EMPLOYEE_TYPE = 'VN';
  const VIETNAMESE_EMPLOYEE_TYPE = 'vietnamese';
  const FOREIGNER_EMPLOYEE_TYPE = 'foreigner';

  const { t } = useTranslation();
  const { isYearEndConfirmed, employeeSsoSetting, employeeSsoRates, ssoRateTypes, ssoCalculatedTypes, nationalityId, nationalities } = props;
  const SSO_CALCULATED_FIXED_AMOUNT_TYPE = 'fixed';

  const [employeeType, setEmployeeType] = useState(employeeSsoSetting?.nationalityClassification || VIETNAMESE_EMPLOYEE_TYPE);
  const [rates, setRates] = useState(employeeSsoRates);
  const [ssoCalculatedType, setSsoCalculatedType] = useState(employeeSsoSetting?.ssoCalculatedType);

  const findNationality = targetNationalityId => {
    if (!targetNationalityId) return null;

    return nationalities.find(nationality => nationality.value === targetNationalityId);
  };

  useEffect(
    () => {
      const isVietnamese = !nationalityId || findNationality(nationalityId)?.code === DEFAULT_EMPLOYEE_TYPE;
      const nationalityType = isVietnamese ? VIETNAMESE_EMPLOYEE_TYPE : FOREIGNER_EMPLOYEE_TYPE;
      setEmployeeType(nationalityType);
    },
    [nationalityId]
  );

  // Recalculate total using useMemo for performance
  const ratioTotal = useMemo(
    () => {
      const filtered = rates.filter(rate => rate.employeeType === employeeType);
      return {
        contributionRateEmployee: filtered.reduce((sum, r) => sum + Number(r.contributionRateEmployee || 0), 0),
        contributionRateEmployer: filtered.reduce((sum, r) => sum + Number(r.contributionRateEmployer || 0), 0)
      };
    },
    [rates, employeeType]
  );

  const handleRateChange = (index, field, value) => {
    const dbValue = toDbFormat(value);
    setRates(prev => prev.map((rate, i) => (i === index ? { ...rate, [field]: dbValue } : rate)));
  };

  return (
    <div>
      <FormSection name="employeeSsoSetting">
        <RadioField
          label={t('features.Employees.components.SocialSecurityContribution.social_insurance_contribution')}
          name="isSocialSecurityContribution"
          options={SOCIAL_SECURITY_TARGETS}
          disabled={isYearEndConfirmed}
        />
        <RadioField
          label={t('features.Employees.components.SocialSecurityContribution.sso_calculated_amount')}
          name="ssoCalculatedType"
          options={ssoCalculatedTypes}
          onChange={e => setSsoCalculatedType(e.target.value)}
        />
        {ssoCalculatedType === SSO_CALCULATED_FIXED_AMOUNT_TYPE && <TextField name="ssoCalculatedAmount" type="number" yen validate={isThaiMoney} />}
        <div className={styles.wrap}>
          <TextField
            label={t('features.Employees.components.SocialSecurityContribution.social_insurance_number')}
            type="text"
            name="socialInsuranceNumber"
            validate={isCorporateNumber}
            maxLength="10"
          />
        </div>
      </FormSection>
      <div>
        <div className="u-mb10">{t('features.Employees.components.SocialSecurityContribution.social_insurance_ratio')}</div>
        <div>
          <table className="m-table-list">
            <thead>
              <tr key="sso-rates-header">
                <th>{t('features.Employees.components.SocialSecurityContribution.insurance_type')}</th>
                <th>{t('features.Employees.components.SocialSecurityContribution.employee_ratio')}</th>
                <th>{t('features.Employees.components.SocialSecurityContribution.employer_ratio')}</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((rate, index) => {
                if (rate.employeeType !== employeeType) return null; // Filter by employee type
                const rateType = ssoRateTypes.find(type => type.value === rate.rateType);
                return (
                  <tr key={`${rateType.value}_${rate.employeeType}`} className={`${rateType.value}_${rate.employeeType}`}>
                    <td>{rateType.label}</td>
                    <td className={socialSecurityStyles.ratioField}>
                      <TextField
                        name={`employeeSsoRates.${index}.contributionRateEmployee`}
                        validate={insuranceRate}
                        format={value => toDisplayFormat(value)}
                        parse={value => toDbFormat(value)}
                        onChange={e => handleRateChange(index, 'contributionRateEmployee', e.target.value)}
                      />
                    </td>
                    <td className={socialSecurityStyles.ratioField}>
                      <TextField
                        name={`employeeSsoRates.${index}.contributionRateEmployer`}
                        validate={insuranceRate}
                        format={value => toDisplayFormat(value)}
                        parse={value => toDbFormat(value)}
                        onChange={e => handleRateChange(index, 'contributionRateEmployer', e.target.value)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="total-row">
              <tr key="sso-rates-totals">
                <td>{t('features.Employees.components.SocialSecurityContribution.total_ratio')}</td>
                <td className="employee-ratio">{`${toDisplayFormat(`${ratioTotal.contributionRateEmployee}`)} %`}</td>
                <td className="employer-ratio">{`${toDisplayFormat(`${ratioTotal.contributionRateEmployer}`)} %`}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

const formSelector = formValueSelector(EMPLOYEE_FORM);
const mapStateToProps = state => ({
  employeeSsoSetting: selector(getItem(REDUCER_NAME, state), 'employeeSsoSetting'),
  employeeSsoRates: selector(getItem(REDUCER_NAME, state), 'employeeSsoRates'),
  ssoRateTypes: getRadioOptions(state, 'ssoRateTypes'),
  ssoCalculatedTypes: getRadioOptions(state, 'ssoCalculatedTypes'),
  nationalityId: formSelector(state, 'employee.nationalityId'),
  nationalityCode: formSelector(state, 'employee.nationalityCode'),
  nationalities: getSelectOptions(state, 'nationalities')
});

export default connect(mapStateToProps)(SocialSecurityContributionSection);
